import { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react'
import { supabase } from '@/integrations/supabase/client'
import { IS_DEMO_MODE } from '@/config/config'

export interface BeneficiarySummary {
    id: string
    name: string
    prenom?: string | null
    nom?: string | null
    numero_suivi?: number | null
    tags: string[] | null
    is_active: boolean
    note_count: number
    stat_count: number
    last_face_to_face: string | null
    last_phone_call: string | null
    last_note_date: string | null
}

export interface Worker {
    id: string
    name: string
    is_referent?: boolean | null
}

interface AppDataContextValue {
    summaries: BeneficiarySummary[]
    workers: Worker[]
    loading: boolean
    refresh: () => Promise<void>
    refreshBeneficiary: (beneficiaryId: string) => Promise<void>
    updateSummaryTags: (beneficiaryId: string, tags: string[]) => void
}

const AppDataContext = createContext<AppDataContextValue | null>(null)

export const useAppData = () => {
    const ctx = useContext(AppDataContext)
    if (!ctx) throw new Error('useAppData must be used within AppDataProvider')
    return ctx
}

export const AppDataProvider = ({ children }: { children: ReactNode }) => {
    const [summaries, setSummaries] = useState<BeneficiarySummary[]>([])
    const [workers, setWorkers] = useState<Worker[]>([])
    const [loading, setLoading] = useState(true)

    const load = useCallback(async () => {
        setLoading(true)
        const [{ data: sumData }, { data: workerData }] = await Promise.all([
            supabase.from('beneficiary_summary').select('*').order('name'),
            supabase.from('workers').select('id, name, is_referent').eq('is_active', true).order('name'),
        ])
        if (sumData) setSummaries(sumData as BeneficiarySummary[])
        if (workerData) setWorkers(workerData as Worker[])
        setLoading(false)
    }, [])

    const refreshBeneficiary = useCallback(async (beneficiaryId: string) => {
        const { data } = await supabase
            .from('beneficiary_summary')
            .select('*')
            .eq('id', beneficiaryId)
            .maybeSingle()
        if (data) {
            setSummaries(prev => {
                const exists = prev.some(s => s.id === beneficiaryId)
                if (exists) return prev.map(s => s.id === beneficiaryId ? data as BeneficiarySummary : s)
                return [...prev, data as BeneficiarySummary].sort((a, b) => a.name.localeCompare(b.name, 'fr'))
            })
        }
    }, [])

    const updateSummaryTags = useCallback((beneficiaryId: string, tags: string[]) => {
        setSummaries(prev => prev.map(s => s.id === beneficiaryId ? { ...s, tags } : s))
    }, [])

    useEffect(() => {
        load()

        // The demo is a read-only snapshot: there is nothing to listen to.
        if (IS_DEMO_MODE) return

        const channel = supabase.channel('app-data-sync')
            .on('postgres_changes', { event: '*', schema: 'public', table: 'notes' }, (payload) => {
                const bid = (payload.new as any)?.beneficiary_id ?? (payload.old as any)?.beneficiary_id
                if (bid) refreshBeneficiary(bid)
            })
            .on('postgres_changes', { event: '*', schema: 'public', table: 'stats' }, (payload) => {
                const bid = (payload.new as any)?.beneficiary_id ?? (payload.old as any)?.beneficiary_id
                if (bid) refreshBeneficiary(bid)
            })
            .subscribe()

        return () => { supabase.removeChannel(channel) }
    }, [load, refreshBeneficiary])

    return (
        <AppDataContext.Provider value={{ summaries, workers, loading, refresh: load, refreshBeneficiary, updateSummaryTags }}>
            {children}
        </AppDataContext.Provider>
    )
}
